// NHTSA Vehicle API — free, no API key required
// https://vpic.nhtsa.dot.gov/api/

export interface NHTSAVehicle {
  vin: string;
  year: number;
  make: string;
  model: string;
  trim: string;
  bodyStyle: string;
  driveType: string;
  fuelType: string;
  engineCylinders: string;
  displacementL: string;
}

const BASE = 'https://vpic.nhtsa.dot.gov/api/vehicles';

function pick(
  results: Array<{ Variable: string; Value: string | null }>,
  variable: string,
): string {
  return results.find((r) => r.Variable === variable)?.Value?.trim() ?? '';
}

function parseResult(
  vin: string,
  results: Array<{ Variable: string; Value: string | null }>,
): NHTSAVehicle | null {
  const year = parseInt(pick(results, 'Model Year'), 10);
  const make = pick(results, 'Make');
  const model = pick(results, 'Model');
  if (!year || !make || !model) return null;
  return {
    vin: vin.toUpperCase(),
    year,
    make,
    model,
    trim: pick(results, 'Trim'),
    bodyStyle: pick(results, 'Body Class'),
    driveType: pick(results, 'Drive Type'),
    fuelType: pick(results, 'Fuel Type - Primary'),
    engineCylinders: pick(results, 'Engine Number of Cylinders'),
    displacementL: pick(results, 'Displacement (L)'),
  };
}

/** Decode a single VIN. Returns null if invalid or NHTSA can't decode it. */
export async function decodeVin(vin: string): Promise<NHTSAVehicle | null> {
  try {
    const res = await fetch(`${BASE}/decodevin/${encodeURIComponent(vin.trim())}?format=json`);
    if (!res.ok) return null;
    const data = await res.json();
    return parseResult(vin, data.Results);
  } catch {
    return null;
  }
}

/**
 * Batch decode up to 50 VINs in a single POST.
 * Returns a map of VIN (uppercased) → decoded vehicle (or null if undecodable).
 */
export async function decodeVinBatch(
  vins: string[],
): Promise<Map<string, NHTSAVehicle | null>> {
  const result = new Map<string, NHTSAVehicle | null>();
  if (vins.length === 0) return result;

  const chunks: string[][] = [];
  for (let i = 0; i < vins.length; i += 50) chunks.push(vins.slice(i, i + 50));

  for (const chunk of chunks) {
    try {
      const body = new URLSearchParams();
      body.set('DATA', chunk.join(';'));
      body.set('format', 'json');

      const res = await fetch(`${BASE}/DecodeVINValuesBatch/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (!res.ok) continue;
      const data = await res.json();

      for (const row of data.Results as Array<Record<string, string>>) {
        const vin = (row['VIN'] ?? '').toUpperCase();
        if (!vin) continue;
        const year = parseInt(row['Model Year'] ?? '', 10);
        const make = (row['Make'] ?? '').trim();
        const model = (row['Model'] ?? '').trim();
        if (!year || !make || !model) {
          result.set(vin, null);
          continue;
        }
        result.set(vin, {
          vin,
          year,
          make,
          model,
          trim: (row['Trim'] ?? '').trim(),
          bodyStyle: (row['Body Class'] ?? '').trim(),
          driveType: (row['Drive Type'] ?? '').trim(),
          fuelType: (row['Fuel Type - Primary'] ?? '').trim(),
          engineCylinders: (row['Engine Number of Cylinders'] ?? '').trim(),
          displacementL: (row['Displacement (L)'] ?? '').trim(),
        });
      }
    } catch {
      // partial failure — leave remaining vins unmapped
    }
  }

  return result;
}

/** Extract all plausible 17-char VINs from raw HTML. */
export function extractVinsFromHtml(html: string): string[] {
  // VINs: 17 alphanumeric chars, no I/O/Q
  const matches = html.match(/\b[A-HJ-NPR-Z0-9]{17}\b/gi) ?? [];
  const seen = new Set<string>();
  return matches
    .map((v) => v.toUpperCase())
    .filter((v) => {
      if (seen.has(v)) return false;
      seen.add(v);
      return true;
    });
}
